import type { FetchRequestError } from "@lib/errors";
import { FetchUserEncodedError } from "@lib/errors/FetchUserEncodedError";
import type {
  SHARED_CONSTANTS_CONVERSATION_TOOL_DEFINITION,
  SHARED_CONSTANTS_CUSTOM_ATTRIBUTE,
  SHARED_CONSTANTS_CUSTOM_EVENT,
  SHARED_CONSTANTS_KNOWLEDGE_SOURCE_SEARCH,
  SHARED_CONSTANTS_WEBHOOK,
} from "@lib/shared_constants";

export type ConversationalToolDefinitionType =
  | "workflow_attribute"
  | "webhook"
  | "custom_attribute"
  | "custom_event"
  | "knowledge_source_search";

export interface WorkflowAttributeAllowedValue {
  value: string | number | boolean;
  description: string;
}

export type WorkflowAttributeDataType = SHARED_CONSTANTS_CONVERSATION_TOOL_DEFINITION["DATA_TYPES"][number];

export interface WorkflowAttributeDefinitionResultObject {
  id: string;
  type: "workflow_attribute";
  name: string;
  description: string;
  data_type: WorkflowAttributeDataType;
  allowed_values: WorkflowAttributeAllowedValue[];
  created_at: string;
  updated_at: string;
  created_by_email: string;
}

export type WebhookRequestMethod = SHARED_CONSTANTS_WEBHOOK["REQUEST_METHODS"][number];

export type WebhookVariableSource = SHARED_CONSTANTS_WEBHOOK["VARIABLE_SOURCES"][number];

export interface WebhookVariable {
  name: string;
  data_type: WorkflowAttributeDataType;
  source: WebhookVariableSource;
  source_id: string | null;
  description: string | null;
  required: boolean;
}

// Request-side variable: the server derives a copied variable's type from its
// source, so the client sends data_type only for agent-sourced variables.
export interface WebhookVariableParam extends Omit<WebhookVariable, "data_type"> {
  data_type: WorkflowAttributeDataType | null;
}

export interface WebhookResponseField {
  name: string;
  path: string;
  description: string;
}

export interface WebhookVariableErrorMeta {
  section: "variables";
  reason: string;
  index?: number;
  property?: string;
}

export interface WebhookDefinitionResultObject {
  id: string;
  type: "webhook";
  name: string;
  description: string;
  request_method: WebhookRequestMethod;
  url: string;
  body: string | null;
  headers: Record<string, string>;
  credential_name: string | null;
  variables: WebhookVariable[];
  response_fields: WebhookResponseField[];
  created_at: string;
  updated_at: string;
  created_by_email: string;
}

export type CustomAttributeSource = SHARED_CONSTANTS_CUSTOM_ATTRIBUTE["SOURCES"][number];

export interface CustomAttributeDefinitionResultObject {
  id: string;
  type: "custom_attribute";
  name: string;
  description: string;
  attribute_name: string;
  source: CustomAttributeSource;
  source_id: string | null;
  created_at: string;
  updated_at: string;
  created_by_email: string;
}

export type CustomEventPropertySource = SHARED_CONSTANTS_CUSTOM_EVENT["PROPERTY_SOURCES"][number];

export interface CustomEventProperty {
  name: string;
  source: CustomEventPropertySource;
  source_id: string | null;
  description: string | null;
  required: boolean;
}

// Per-property errors point at `properties.{index}.{property}`; list-level errors carry no meta.
export interface CustomEventPropertyErrorMeta {
  index?: number;
  property?: string;
}

export interface CustomEventDefinitionResultObject {
  id: string;
  type: "custom_event";
  name: string;
  description: string;
  event_name: string;
  properties: CustomEventProperty[];
  created_at: string;
  updated_at: string;
  created_by_email: string;
}

export interface KnowledgeSourceSearchDefinitionResultObject {
  id: string;
  type: "knowledge_source_search";
  name: string;
  description: string;
  knowledge_source_definition_id: string;
  excluded_fields: string[];
  created_at: string;
  updated_at: string;
  created_by_email: string;
}

export type ConversationalToolDefinitionResultObject =
  | WorkflowAttributeDefinitionResultObject
  | WebhookDefinitionResultObject
  | CustomAttributeDefinitionResultObject
  | CustomEventDefinitionResultObject
  | KnowledgeSourceSearchDefinitionResultObject;

export interface ConversationalToolDefinitionIdResult {
  result: ConversationalToolDefinitionResultObject;
}

export interface GetConversationalToolDefinitionIdProps {
  type: ConversationalToolDefinitionType;
  appGroupId: string;
  definitionId: string;
}

export interface UpdateWorkflowAttributeDefinitionIdProps {
  type: "workflow_attribute";
  appGroupId: string;
  definitionId: string;
  description: string;
  allowedValues: WorkflowAttributeAllowedValue[];
}

export interface UpdateWebhookDefinitionIdProps {
  type: "webhook";
  appGroupId: string;
  definitionId: string;
  description: string;
  requestMethod: WebhookRequestMethod;
  url: string;
  body: string | null;
  headers: Record<string, string>;
  credentialName: string | null;
  variables: WebhookVariableParam[];
  responseFields: WebhookResponseField[];
}

export interface UpdateCustomAttributeDefinitionIdProps {
  type: "custom_attribute";
  appGroupId: string;
  definitionId: string;
  description: string;
  source: CustomAttributeSource;
  sourceId: string | null;
}

export interface UpdateCustomEventDefinitionIdProps {
  type: "custom_event";
  appGroupId: string;
  definitionId: string;
  description: string;
  properties: CustomEventProperty[];
}

export interface UpdateKnowledgeSourceSearchDefinitionIdProps {
  type: "knowledge_source_search";
  appGroupId: string;
  definitionId: string;
  description: string;
  excludedFields: string[];
}

export type UpdateConversationalToolDefinitionIdProps =
  | UpdateWorkflowAttributeDefinitionIdProps
  | UpdateWebhookDefinitionIdProps
  | UpdateCustomAttributeDefinitionIdProps
  | UpdateCustomEventDefinitionIdProps
  | UpdateKnowledgeSourceSearchDefinitionIdProps;

export interface DeleteConversationalToolDefinitionIdProps {
  type: ConversationalToolDefinitionType;
  appGroupId: string;
  definitionId: string;
}

export type UpdateConversationalToolDefinitionInvalidDescriptionError = FetchUserEncodedError<
  SHARED_CONSTANTS_CONVERSATION_TOOL_DEFINITION["ERROR_CODES"]["INVALID_DESCRIPTION"]
>;
export type UpdateConversationalToolDefinitionInvalidAllowedValuesError = FetchUserEncodedError<
  SHARED_CONSTANTS_CONVERSATION_TOOL_DEFINITION["ERROR_CODES"]["INVALID_ALLOWED_VALUES"]
>;

export type UpdateWebhookDefinitionIdErrors =
  | UpdateConversationalToolDefinitionInvalidDescriptionError
  | FetchUserEncodedError<SHARED_CONSTANTS_WEBHOOK["ERROR_CODES"]["INVALID_REQUEST_METHOD"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_WEBHOOK["ERROR_CODES"]["INVALID_URL"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_WEBHOOK["ERROR_CODES"]["INVALID_BODY"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_WEBHOOK["ERROR_CODES"]["INVALID_HEADERS"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_WEBHOOK["ERROR_CODES"]["INVALID_URL_TEMPLATE_UNDECLARED_VARIABLE"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_WEBHOOK["ERROR_CODES"]["INVALID_URL_TEMPLATE_LIQUID_TAG"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_WEBHOOK["ERROR_CODES"]["INVALID_URL_TEMPLATE_LITERAL"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_WEBHOOK["ERROR_CODES"]["INVALID_URL_TEMPLATE_SYNTAX"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_WEBHOOK["ERROR_CODES"]["INVALID_BODY_TEMPLATE_UNDECLARED_VARIABLE"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_WEBHOOK["ERROR_CODES"]["INVALID_BODY_TEMPLATE_LIQUID_TAG"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_WEBHOOK["ERROR_CODES"]["INVALID_BODY_TEMPLATE_LITERAL"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_WEBHOOK["ERROR_CODES"]["INVALID_BODY_TEMPLATE_SYNTAX"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_WEBHOOK["ERROR_CODES"]["INVALID_HEADERS_TEMPLATE_UNDECLARED_VARIABLE"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_WEBHOOK["ERROR_CODES"]["INVALID_HEADERS_TEMPLATE_LIQUID_TAG"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_WEBHOOK["ERROR_CODES"]["INVALID_HEADERS_TEMPLATE_LITERAL"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_WEBHOOK["ERROR_CODES"]["INVALID_HEADERS_TEMPLATE_SYNTAX"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_WEBHOOK["ERROR_CODES"]["INVALID_VARIABLES"], WebhookVariableErrorMeta>
  | FetchUserEncodedError<SHARED_CONSTANTS_WEBHOOK["ERROR_CODES"]["INVALID_RESPONSE_FIELDS"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_WEBHOOK["ERROR_CODES"]["INVALID_CREDENTIAL"]>;

export type UpdateCustomAttributeDefinitionIdErrors =
  | UpdateConversationalToolDefinitionInvalidDescriptionError
  | FetchUserEncodedError<SHARED_CONSTANTS_CUSTOM_ATTRIBUTE["ERROR_CODES"]["INVALID_ATTRIBUTE"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_CUSTOM_ATTRIBUTE["ERROR_CODES"]["INVALID_SOURCE"]>;

export type UpdateCustomEventDefinitionIdErrors =
  | UpdateConversationalToolDefinitionInvalidDescriptionError
  | FetchUserEncodedError<SHARED_CONSTANTS_CUSTOM_EVENT["ERROR_CODES"]["INVALID_EVENT_NAME"]>
  | FetchUserEncodedError<
      SHARED_CONSTANTS_CUSTOM_EVENT["ERROR_CODES"]["INVALID_PROPERTIES"],
      CustomEventPropertyErrorMeta
    >;

export type UpdateKnowledgeSourceSearchDefinitionIdErrors =
  | UpdateConversationalToolDefinitionInvalidDescriptionError
  | FetchUserEncodedError<SHARED_CONSTANTS_KNOWLEDGE_SOURCE_SEARCH["ERROR_CODES"]["INVALID_KNOWLEDGE_SOURCE"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_KNOWLEDGE_SOURCE_SEARCH["ERROR_CODES"]["INVALID_EXCLUDED_FIELDS"]>;

export type UpdateConversationalToolDefinitionIdErrors =
  | UpdateConversationalToolDefinitionInvalidDescriptionError
  | UpdateConversationalToolDefinitionInvalidAllowedValuesError
  | UpdateWebhookDefinitionIdErrors
  | UpdateCustomAttributeDefinitionIdErrors
  | UpdateCustomEventDefinitionIdErrors
  | UpdateKnowledgeSourceSearchDefinitionIdErrors;

export const isUpdateConversationalToolDefinitionIdError = (
  error: FetchRequestError
): error is UpdateConversationalToolDefinitionIdErrors => {
  return error instanceof FetchUserEncodedError;
};

export const isUpdateWebhookDefinitionIdError = (
  error: FetchRequestError
): error is UpdateWebhookDefinitionIdErrors => {
  return error instanceof FetchUserEncodedError;
};

export const isUpdateCustomAttributeDefinitionIdError = (
  error: FetchRequestError
): error is UpdateCustomAttributeDefinitionIdErrors => {
  return error instanceof FetchUserEncodedError;
};

export const isUpdateCustomEventDefinitionIdError = (
  error: FetchRequestError
): error is UpdateCustomEventDefinitionIdErrors => {
  return error instanceof FetchUserEncodedError;
};

export const isUpdateKnowledgeSourceSearchDefinitionIdError = (
  error: FetchRequestError
): error is UpdateKnowledgeSourceSearchDefinitionIdErrors => {
  return error instanceof FetchUserEncodedError;
};

export type DeleteConversationalToolDefinitionIdErrors = FetchUserEncodedError<
  SHARED_CONSTANTS_CONVERSATION_TOOL_DEFINITION["ERROR_CODES"]["DELETE_IN_USE"]
>;

export const isDeleteConversationalToolDefinitionIdError = (
  error: FetchRequestError
): error is DeleteConversationalToolDefinitionIdErrors => {
  return error instanceof FetchUserEncodedError;
};
